import "./LoginPage.scoped.scss";

import { useFormik } from "formik";
import { Link, navigate } from "raviger";
import React, { useEffect, useRef, useState } from "react";
import { isExpired } from "react-jwt";
import * as Yup from "yup";

import useJwtTokenData from "core/custom-hooks/useJwtTokenData";
import useStore from "core/custom-hooks/useStore";

import PreAuthenticationField from "components/PreAuthenticationField/PreAuthenticationField";
import * as Utils from "core/common/utils";
import * as Validations from "core/common/validations";
import { navigateReturnUrl } from "core/route/index";
import {
    setAdminOtpLoginTempData,
    setCurrentBusiness,
    setFromLoginPage,
    setMobileAppWrapperData,
    setOpenBiometricAsDefault,
    setPopup,
} from "core/store/actionCreators";
import AuthenticationFormHeader from "../components/AuthenticationFormHeader/AuthenticationFormHeader";
import AuthenticationFormLogo from "../components/AuthenticationFormLogo/AuthenticationFormLogo";

import { openPopup } from "components/Popup/Popup";
import * as MarketingAutomation from "core/common/marketing-automation/be2see";
import * as TagManager from "core/common/seo/tagManager";
import mobileAppEventType from "core/common/types/mobileAppEventType";

import LoginAppsDownload from "components/LoginAppsDownload/LoginAppsDownload";
import LoginContactUs from "components/LoginContactUs/LoginContactUs";

import {
    ACCESS_TOKEN_LOCAL_STORAGE_KEY,
} from "core/common/constants/localstorage";
const LoginPage = () => {
    const { dispatch, state, authRepository } = useStore();
    const {
        globalEventEmitter,
        recaptcha,
        isMobileApp,
        mobileAppWrapperData,
        adminOtpLoginTempData,
        openBiometricAsDefault,
        hash
    } = state;
    const {
        mobileEventEmitter,
        hebrewBiometryType,
        isBiometricLoginEnabled,
    } = mobileAppWrapperData;


    const { recaptchaRef } = recaptcha;
    const { hasAdminRole } = useJwtTokenData();

    const [errorMsg, setErrorMsg] = useState("");
    const [recaptchaResponse, setRecaptchaResponse] = useState("");
    const [hideInputText, setHideInputText] = useState(false);
    const [isFirstFailure, setIsFirstFailure] = useState(true);
    const isAndroid = !window.navigator.userAgent?.includes("appleapp");
    const submitRef = useRef();

    const biometricLoginText =
        hebrewBiometryType && `כניסה באמצעות ${hebrewBiometryType}`;


    const handleOtpLoginClick = () => {
        if (!formik.values.terms) {
            formik.setTouched({ ...formik.touched, terms: true });
            return; 
        }
        TagManager.sendGoogleEvent({
            category: "password login page",
            action: "move to otp login page",
        });



        navigate("/otplogin");
    };

    useEffect(() => {

        const token = sessionStorage.getItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
        if (token) {
            const expired = isExpired(token);
            if (!expired) {
                return navigate("/home");
            }
        }

    }, []);

    useEffect(() => {
        dispatch(setFromLoginPage(true));
    }, [setFromLoginPage]);

    useEffect(() => {
        if (!adminOtpLoginTempData?.email || !adminOtpLoginTempData?.sentTo) return;
        return navigate("/otp-admin-login");
    }, [adminOtpLoginTempData?.email, adminOtpLoginTempData?.sentTo]);

    const handleNormalLogin = async ({ email, password }) => {
        if (recaptchaRef && Utils.recaptchaExecute(recaptchaResponse, recaptchaRef))
            return;

        try {
            Utils.loadingWrapper(dispatch, async () => {
                const { data: responseData } =
                    (await authRepository.loginWithEmailAndPassword(
                        email,
                        password,
                        recaptchaResponse,
                        hash
                    )) || {};
                const { message } = responseData || {};
                const { masked_mobile_phone, otp_session_id } = responseData.data || {};
                if (!Utils.isSuccessfulRequest(dispatch, responseData)) {
                    TagManager.sendGoogleEvent({
                        category: "password login page",
                        action: "Login button",
                        label: "fails",
                    });
                    MarketingAutomation.sendEvent({ type: "Click", name: "Login_Error" });

                    if (isFirstFailure) {
                        openPopup(
                            dispatch,
                            "שגיאה בהתחברות",
                            "אחד מפרטי ההזדהות שהזנת שגויים. במקרה ושכחת סיסמה, באפשרותך לקבל קוד חד פעמי ישירות לנייד או ללחוץ על שכחתי סיסמה", //T("MSG_CREDENTIALS_INCORRECT_USE_OTP_OR_FORGET_PASSWORD"),
                            "הבנתי, תודה",
                            () => popupFailLoginDispatchHandler(dispatch, TagManager),
                            () => dispatch(setPopup({ isOpen: false })),
                            "באפשרותך לקבל קוד חד פעמי ישירות לנייד",
                            null,
                            () => otpLoginDispatchHandler(TagManager)
                        );
                        setIsFirstFailure(false);
                    } else {
                        setErrorMsg(
                            message ||
                            "שגיאה בפרטי התחברות"
                        );
                        setTimeout(() => {
                            setErrorMsg("");
                        }, 5000);
                    }

                    return;
                }

                MarketingAutomation.sendEvent({
                    type: "Click",
                    name: "Password_login",
                });

                if (otp_session_id) {
                    return dispatch(
                        setAdminOtpLoginTempData({
                            email,
                            password,
                            otpSessionId: otp_session_id,
                            sentTo: masked_mobile_phone,
                        })
                    );
                }

                if (hasAdminRole()) {
                    TagManager.sendGoogleEvent({
                        category: "password login page",
                        action: "Login button",
                        label: "succeeded",
                    });
                    return navigate("/admin-account-selection");
                }

                TagManager.sendGoogleEvent({
                    category: "password login page",
                    action: "Login button",
                    label: "succeeded",
                });

                return navigateReturnUrl();
            });
        } finally {
        }
    };

    const otpLoginDispatchHandler = (tagManager) => {
        tagManager.sendGoogleEvent({
            category: "fails login popup",
            action: "click on button",
            label: "get verafication code to mobile",
        });
        navigate("/otplogin");
    };

    const popupFailLoginDispatchHandler = (dispatch, tagManager) => {
        tagManager.sendGoogleEvent({
            category: "fails login popup",
            action: "click on button",
            label: "got it thank you",
        });
        dispatch(setPopup({ isOpen: false }));
    };

    const handleBiometricLoginLogin = async ({ email, password }) => {
        if (Utils.recaptchaExecute(recaptchaResponse, recaptchaRef)) return;

        try {
            Utils.loadingWrapper(dispatch, async () => {
                const { data: responseData, status } =
                    (await authRepository.loginWithEmailAndPassword(
                        email,
                        password,
                        recaptchaResponse,
                        hash
                    )) || {};

                const { masked_mobile_phone, otp_session_id } = responseData.data || {};

                setHideInputText(false);
                setTimeout(() => formik.resetForm(), 500);

                if (!Utils.isSuccessfulRequest(dispatch, responseData)) {
                    TagManager.sendGoogleEvent({
                        category: "password login page",
                        action: "Login button",
                        label: "fails",
                    });

                    if (status === 401) {
                        mobileEventEmitter(mobileAppEventType.generalMessage, {
                            title:
                                "לא ניתן להזדהות באמצעי זה" /*T("H_IMPOSSIBLE_TO_IDENTIFY")*/,
                            message:
                                "יש להגדיר מחדש או להיכנס באמצעי אחר" /*T("MSG_DEFINE_AGAIN_OR_RELOGIN")*/,
                        });
                        return mobileEventEmitter(mobileAppEventType.biometricsDisabling);
                    }

                    return mobileEventEmitter(mobileAppEventType.generalMessage, {
                        title:
                            "לא ניתן להזדהות באמצעי זה" /*T("H_IMPOSSIBLE_TO_IDENTIFY")*/,
                    });
                }

                if (otp_session_id) {
                    return dispatch(
                        setAdminOtpLoginTempData({
                            email,
                            password,
                            otpSessionId: otp_session_id,
                            sentTo: masked_mobile_phone,
                        })
                    );
                }

                MarketingAutomation.sendEvent({
                    type: "Click",
                    name: "Finger_Print_login",
                });

                if (hasAdminRole()) {
                    TagManager.sendGoogleEvent({
                        category: "password login page",
                        action: "Login button",
                        label: "succeeded",
                    });
                    return navigate("/admin-account-selection");
                }

                TagManager.sendGoogleEvent({
                    category: "password login page",
                    action: "Login button",
                    label: "succeeded",
                });

                return navigateReturnUrl();
            });

        } finally { }
    };

    const handleLoginSubmit = async ({ email, password }) => {
        const params = { email, password };
        const isBiometricLogin = hideInputText;

        const handler = isBiometricLogin
            ? handleBiometricLoginLogin
            : handleNormalLogin;
        await handler(params);
    };

    const formik = useFormik({
    initialValues: {
        email: sessionStorage.getItem("__em") || "",
        password: "",
        terms: true,
    },
    validationSchema: Yup.object({
        email: Validations.email,
        password: Validations.emptyPassword,
        terms: Yup.boolean().oneOf(
            [true],
            "חובה לאשר את תנאי השימוש ומדיניות הפרטיות"
        ),
    }),
    onSubmit: handleLoginSubmit,
});

    const emailOptions = {
        formik: formik,
        fieldName: "email",
        label: "שם משתמש (כתובת מייל)", 
        className: hideInputText && "hide_text",
    };

    const passwordOptions = {
        formik: formik,
        fieldName: "password",
        label: "סיסמה" ,
        inputType: "password",
        className: hideInputText && "hide_text",
        shouldAllowTogglePasswordVisibility: true,
    };

    //#region Hooks

    useEffect(() => {
        if (!isMobileApp) return;

        const handler = ({ email, password }) => {
            if (email && password) {
                formik.setValues({ email, password });
                setHideInputText(true);
            }
        };

        globalEventEmitter.on(mobileAppEventType.loginData, handler);
    }, [isMobileApp]);

    useEffect(() => {
        if (hideInputText) {
            document.getElementById("submit").click();
        }

        // eslint-disable-next-line
    }, [hideInputText]);

    useEffect(() => {
        Utils.recaptchaHelper(dispatch, setRecaptchaResponse, submitRef);

        dispatch(setCurrentBusiness(null));


        return () =>
            dispatch(
                setMobileAppWrapperData({
                    biometricsRegistrationEmail: null,
                    biometricsRegistrationPassword: null,
                })
            );

        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (biometricLoginText && isBiometricLoginEnabled && !openBiometricAsDefault) {
            dispatch(setOpenBiometricAsDefault(true))
            mobileEventEmitter(
                mobileAppEventType.showSensor,
                {}
            );
        }
    }, [])

    //#endregion

    return (
        <div className="loggedin_wrap center-content">
            <div className="loggedin_box">
                <AuthenticationFormLogo />
                <div className="loggedin_form_wrap">
                    <AuthenticationFormHeader
                        title="כניסה באמצעות סיסמה"
                    />
                    <form onSubmit={formik.handleSubmit}>
                        <div className="loggedin_form_box">
                            <div className="loggedin_form_field_box">
                                <PreAuthenticationField options={emailOptions} />
                                <PreAuthenticationField options={passwordOptions} />
                                {errorMsg && <span className="error_msg">{errorMsg}</span>}
                            </div>
                            <div className="forgot_password_wrap">
                                <Link
                                    className="forgot_password_btn"
                                    onClick={() =>
                                        TagManager.sendGoogleEvent({
                                            category: "password login page",
                                            action: "Forgot password",
                                        })
                                    }
                                    href="/password/forget"
                                >
                                    שכחתי סיסמה
                                </Link>
                            </div>
                            <label className="terms_checkbox">
                                <input
                                    type="checkbox"
                                    name="terms"
                                    className="terms_input"
                                    checked={formik.values.terms}
                                    onChange={formik.handleChange}
                                />
                                <span className="terms_text">
                                    אני מאשר/ת את{" "}
                                    <a
                                        href="https://zapgroup.co.il/terms/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="terms_link"
                                    >
                                        תנאי השימוש
                                    </a>{" "}
                                    ו{" "}
                                    <a
                                        href="https://img.zapgroup.co.il/PrivacyPolicy.pdf"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="terms_link"
                                    >
                                        מדיניות הפרטיות
                                    </a>{" "}
                                    של קבוצת זאפ
                                </span>
                            </label>
                            {formik.errors.terms && formik.touched.terms && (
                                <div className="error_msg">{formik.errors.terms}</div>
                            )}

                            <div className="loggedin_form_submit">
                                <div className="loggedin_send_btn">
                                    <input
                                        onClick={(_) => {
                                            const type = "Click";
                                            let name = "Website_Login_Desktop";
                                            if (isMobileApp) {
                                                name = "App_Login_Mobile";
                                            } else if (Utils.isMobile()) {
                                                name = "Website_Login_Mobile";
                                            }

                                            MarketingAutomation.sendEvent({ type, name });
                                        }}
                                        ref={(el) => {
                                            submitRef.current = el;
                                        }}
                                        className="update_btn"
                                        id="submit"
                                        type="submit"
                                        name="submit"
                                        value={"התחבר/י"}
                                    />
                                </div>
                                <p>
                                    <span>{"או"}</span>
                                </p>
                                <div className="login_btn_wrap">
                                    <button
                                        type="button"
                                        className="login_btn"
                                        onClick={handleOtpLoginClick}
                                    >
                                        כניסה באמצעות קוד לנייד
                                    </button>
                                </div>
                                {biometricLoginText && (
                                    <div className="biometric-identify">
                                        <div className="circle-identify-icon">
                                            <div
                                                className={
                                                    isAndroid
                                                        ? "loggedin_send_img_android"
                                                        : "loggedin_send_img_ios"
                                                }
                                            >
                                                <input
                                                    onClick={() => {
                                                        if (isBiometricLoginEnabled) {
                                                            mobileEventEmitter(
                                                                mobileAppEventType.showSensor,
                                                                {}
                                                            );
                                                        } else {
                                                            navigate("/biometrics-registration");
                                                        }
                                                    }}
                                                    className="login_btn_biometric"
                                                    id="finger_print"
                                                    type="button"
                                                    name="submit"
                                                />
                                            </div>
                                        </div>
                                        <div className="biometric-text">כניסת ביומטרית</div>
                                    </div>
                                )}
                                <span className="wave">
                                    {!isMobileApp && <LoginAppsDownload />}
                                    <LoginContactUs isMobileApp={isMobileApp} />
                                </span>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
